import { DocumentResponse } from '../services/apiService';

interface StreamingCitationState {
  processedContent: string;
  documentMap: Map<number, number>; // original doc number -> normalized doc number
  referencedDocuments: DocumentResponse[];
  nextNormalizedNumber: number;
}

/**
 * Processes citations in real-time during streaming
 * Maintains state to ensure consistent numbering as content streams in
 */
export class StreamingCitationProcessor {
  private state: StreamingCitationState;
  private documents: DocumentResponse[];
  private lastProcessedLength: number = 0;

  constructor(documents: DocumentResponse[]) {
    this.documents = documents;
    this.state = {
      processedContent: '',
      documentMap: new Map(),
      referencedDocuments: [],
      nextNormalizedNumber: 1
    };
  }

  /**
   * Process new content chunk during streaming
   * Optimized to only reprocess when necessary
   */
  processChunk(fullContent: string): StreamingCitationState {
    if (fullContent.length === 0) {
      return this.state;
    }

    // Only reprocess if we have new content that might contain citations
    const newContent = fullContent.slice(this.lastProcessedLength);
    
    // Check if new content contains potential citation markers (including complex citations)
    const hasPotentialCitation = /\[Document\s*\d*|Document\s+\d+[^\]]*\]?/.test(newContent);
    
    if (!hasPotentialCitation && this.lastProcessedLength > 0) {
      // No potential citations in new content, just append processed new content
      const cleanNewContent = this.removeDocumentCitationsFromText(newContent);
      this.state.processedContent += cleanNewContent;
      this.lastProcessedLength = fullContent.length;
      return { ...this.state };
    }

    // Only do full reprocessing when we detect potential citations
    const tempState = {
      processedContent: '',
      documentMap: new Map<number, number>(),
      referencedDocuments: [] as DocumentResponse[],
      nextNormalizedNumber: 1
    };

    // Process the entire content from scratch
    const processedContent = this.processContentForCitations(fullContent, tempState);
    
    // Update our state
    this.state = {
      processedContent,
      documentMap: tempState.documentMap,
      referencedDocuments: tempState.referencedDocuments,
      nextNormalizedNumber: tempState.nextNormalizedNumber
    };
    
    this.lastProcessedLength = fullContent.length;

    return { ...this.state };
  }

  /**
   * Process entire content for citations with given state
   */
  private processContentForCitations(content: string, tempState: any): string {
    if (!content) return content;

    // Find all document references in the content (including complex citations)
    const citationRegex = /\[Document\s+\d+(?:[^\]]*?)\]/g;
    const documentNumberRegex = /Document\s+(\d+)/g;
    
    let processedContent = content;
    let match;

    // Process each citation found in the content
    while ((match = citationRegex.exec(content)) !== null) {
      const fullMatch = match[0];
      const docNumbers: number[] = [];
      
      // Extract all document numbers from this citation
      let docMatch;
      documentNumberRegex.lastIndex = 0; // Reset regex
      
      while ((docMatch = documentNumberRegex.exec(fullMatch)) !== null) {
        const docNum = parseInt(docMatch[1], 10);
        docNumbers.push(docNum);
      }
      
      // Extract additional citation info (like "section 5.2")
      const additionalInfo = this.extractAdditionalCitationInfo(fullMatch);

      // Process each document number
      const normalizedRefs: string[] = [];
      
      for (const docNum of docNumbers) {
        let normalizedNum = tempState.documentMap.get(docNum);
        
        // If this is the first time we see this document, assign it the next number
        if (normalizedNum === undefined) {
          normalizedNum = tempState.nextNormalizedNumber;
          tempState.documentMap.set(docNum, normalizedNum);
          tempState.nextNormalizedNumber++;
          
          // Add the document to our referenced documents list
          const documentIndex = docNum - 1; // Documents are 0-indexed but referenced as 1-indexed
          if (documentIndex >= 0 && documentIndex < this.documents.length) {
            tempState.referencedDocuments.push(this.documents[documentIndex]);
          }
        }
        
        // Create the markdown citation with document name and additional info
        const documentIndex = normalizedNum - 1;
        const docName = tempState.referencedDocuments[documentIndex]?.document || `Document ${normalizedNum}`;
        const citationText = additionalInfo ? `${normalizedNum}, ${additionalInfo}` : `${normalizedNum}`;
        normalizedRefs.push(`[${citationText}](#doc-${normalizedNum} "${docName}")`);
      }
      
      // Replace the original citation with the processed one
      const replacement = normalizedRefs.join(', ');
      processedContent = processedContent.replace(fullMatch, replacement);
      
      // Reset regex for next iteration
      citationRegex.lastIndex = 0;
    }

    return processedContent;
  }

  /**
   * Process new content chunk for citations (legacy method)
   */
  private processNewContentForCitations(content: string): string {
    if (!content) return content;

    // Find all document references in the new content
    const citationRegex = /\[Document\s+(\d+)(?:\s*,\s*Document\s+(\d+))*\]/g;
    const documentNumberRegex = /Document\s+(\d+)/g;
    
    let processedContent = content;
    let match;

    // Process each citation found in the new content
    while ((match = citationRegex.exec(content)) !== null) {
      const fullMatch = match[0];
      const docNumbers: number[] = [];
      
      // Extract all document numbers from this citation
      let docMatch;
      documentNumberRegex.lastIndex = 0; // Reset regex
      
      while ((docMatch = documentNumberRegex.exec(fullMatch)) !== null) {
        const docNum = parseInt(docMatch[1], 10);
        docNumbers.push(docNum);
      }

      // Process each document number
      const normalizedRefs: string[] = [];
      
      for (const docNum of docNumbers) {
        let normalizedNum = this.state.documentMap.get(docNum);
        
        // If this is the first time we see this document, assign it the next number
        if (normalizedNum === undefined) {
          normalizedNum = this.state.nextNormalizedNumber;
          this.state.documentMap.set(docNum, normalizedNum);
          this.state.nextNormalizedNumber++;
          
          // Add the document to our referenced documents list
          const documentIndex = docNum - 1; // Documents are 0-indexed but referenced as 1-indexed
          if (documentIndex >= 0 && documentIndex < this.documents.length) {
            this.state.referencedDocuments.push(this.documents[documentIndex]);
          }
        }
        
        // Create the markdown citation with document name
        const documentIndex = normalizedNum - 1;
        const docName = this.state.referencedDocuments[documentIndex]?.document || `Document ${normalizedNum}`;
        normalizedRefs.push(`[${normalizedNum}](#doc-${normalizedNum} "${docName}")`);
      }
      
      // Replace the original citation with the processed one
      const replacement = normalizedRefs.join(', ');
      processedContent = processedContent.replace(fullMatch, replacement);
      
      // Reset regex for next iteration
      citationRegex.lastIndex = 0;
    }

    return processedContent;
  }

  /**
   * Extracts additional citation information (like "section 5.2") from a citation
   */
  private extractAdditionalCitationInfo(citation: string): string {
    // Remove the brackets and extract everything after the document number(s)
    const withoutBrackets = citation.slice(1, -1); // Remove [ and ]
    
    // Find the last Document reference and get everything after it
    const lastDocumentMatch = withoutBrackets.match(/.*Document\s+\d+(.*)$/);
    if (lastDocumentMatch && lastDocumentMatch[1]) {
      const additionalInfo = lastDocumentMatch[1].trim();
      // Remove leading comma if present
      return additionalInfo.replace(/^,\s*/, '');
    }
    
    return '';
  }

  /**
   * Remove document citations from text without processing
   */
  private removeDocumentCitationsFromText(content: string): string {
    if (!content) return content;
    
    // Remove [Document X, Document Y] patterns including additional info
    const citationRegex = /\[Document\s+\d+(?:[^\]]*?)\]/g;
    return content.replace(citationRegex, '');
  }

  /**
   * Get current state
   */
  getState(): StreamingCitationState {
    return { ...this.state };
  }

  /**
   * Reset processor for new content
   */
  reset(): void {
    this.state = {
      processedContent: '',
      documentMap: new Map(),
      referencedDocuments: [],
      nextNormalizedNumber: 1
    };
    this.lastProcessedLength = 0;
  }
}

/**
 * Simple function to remove citations during streaming (when Smart Citations is disabled)
 */
export function removeDocumentCitationsStreaming(content: string): string {
  if (!content) return content;
  
  // Remove [Document X, Document Y] patterns including additional info
  const citationRegex = /\[Document\s+\d+(?:[^\]]*?)\]/g;
  return content.replace(citationRegex, '');
}
